import type {
  AdminListResult,
  AdminOverview,
  AdminProjectDetail,
  AdminRoomDetail,
  AdminRoomQuality,
  AdminRunDetail,
  AdminSearchResult,
  AdminStats,
  AdminUserDetail,
} from '@repo/contracts';

import { apiFetch } from '../../api/client';
import type { ListQuery } from './types';

/**
 * One root, so signing out or switching anything can drop every admin query at
 * once — and so nothing in here collides with the product's `canvas` keys.
 */
export const adminKeys = {
  all: ['admin'] as const,
  projectDetail: (projectId: string) =>
    [...adminKeys.all, 'project', projectId] as const,
  overview: (days: number) => [...adminKeys.all, 'overview', days] as const,
  quality: () => [...adminKeys.all, 'quality'] as const,
  roomDetail: (roomId: string) => [...adminKeys.all, 'room', roomId] as const,
  list: (path: string, query: ListQuery) =>
    [...adminKeys.all, 'list', path, query] as const,
  runDetail: (feature: string, id: string) =>
    [...adminKeys.all, 'run', feature, id] as const,
  search: (term: string) => [...adminKeys.all, 'search', term] as const,
  stats: () => [...adminKeys.all, 'stats'] as const,
  userDetail: (uid: string) => [...adminKeys.all, 'user', uid] as const,
  whoami: () => [...adminKeys.all, 'whoami'] as const,
};

/**
 * A `ListQuery` as a query string.
 *
 * Filters go out as repeated `filter=key:value` rather than a param per
 * column, because the columns belong to a resource and the DTO on the other end
 * cannot know them. Empty values are dropped so a default-state URL is clean.
 */
function toSearchParams(query: ListQuery): string {
  const params = new URLSearchParams();
  params.set('page', String(query.page));
  params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  params.set('sortDir', query.sortDir);
  if (query.q) params.set('q', query.q);
  for (const [key, value] of Object.entries(query.filters)) {
    if (value) params.append('filter', `${key}:${value}`);
  }
  for (const key of [
    'accountId',
    'projectId',
    'sessionId',
    'userId',
  ] as const) {
    const value = query[key];
    if (value) params.set(key, value);
  }
  return params.toString();
}

export function listResource<TRow>(
  path: string,
  query: ListQuery,
): Promise<AdminListResult<TRow>> {
  return apiFetch<AdminListResult<TRow>>(
    `/admin/${path}?${toSearchParams(query)}`,
  );
}

export function fetchOverview(days: number): Promise<AdminOverview> {
  return apiFetch<AdminOverview>(`/admin/overview?days=${String(days)}`);
}

export function fetchRoomDetail(roomId: string): Promise<AdminRoomDetail> {
  return apiFetch<AdminRoomDetail>(
    `/admin/rooms/${encodeURIComponent(roomId)}`,
  );
}

export function fetchQuality(): Promise<AdminRoomQuality> {
  return apiFetch<AdminRoomQuality>('/admin/quality');
}

export function fetchStats(): Promise<AdminStats> {
  return apiFetch<AdminStats>('/admin/stats');
}

export function fetchWhoami(): Promise<{ isAdmin: boolean }> {
  return apiFetch<{ isAdmin: boolean }>('/admin/whoami');
}

export function fetchUserDetail(uid: string): Promise<AdminUserDetail> {
  return apiFetch<AdminUserDetail>(`/admin/users/${encodeURIComponent(uid)}`);
}

export function fetchProjectDetail(
  projectId: string,
): Promise<AdminProjectDetail> {
  return apiFetch<AdminProjectDetail>(
    `/admin/projects/${encodeURIComponent(projectId)}`,
  );
}

export function fetchRunDetail(
  feature: string,
  id: string,
): Promise<AdminRunDetail> {
  return apiFetch<AdminRunDetail>(
    `/admin/runs/${encodeURIComponent(feature)}/${encodeURIComponent(id)}`,
  );
}

export function search(term: string): Promise<AdminSearchResult> {
  return apiFetch<AdminSearchResult>(
    `/admin/search?q=${encodeURIComponent(term)}`,
  );
}
